import * as THREE from 'three';
import gsap from 'gsap';    
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Stats from 'stats.js';

// ---------- Variabili globali ----------
let scene, camera, renderer, instancedMesh, controls;
let instanceData = [];
let selectedInstanceId = null;

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
const stats = new Stats();

const datasetsPerLocation = {
    cala_fico: ["data.csv"],
    corno: ["data.csv"],
    genio: ["-3m_id_611.csv"],
    giardino_del_re: ["data.csv"],
    isola_di_stea: ["data.csv"],
    la_punta: ["data.csv"],
    la_caletta: ["-5m_id_620.csv"],
    mezza_luna: ["data.csv"],
    punta_nera: ["data.csv"],
    punta_delle_oche: ["data.csv"],
    relitto: ["data.csv"],
    spalmatore: ["data.csv"],
    tacche_bianche: ["-17m_id_616.csv"],
    tempio: ["-30m_id_612.csv"],
    tonnara: ["-3m_id_622.csv", "-31m_id_613.csv"]
};

const locationSelect = document.getElementById('locationSelector');
const datasetSelect = document.getElementById('datasetSelector');
const temperaturaMediaDiv = document.getElementById('temperaturaMediaData');
const temperaturaSelezionataDataDiv = document.getElementById('temperaturaSelezionataData');
const temperatureDisplay = document.getElementById('temperaturaSelezionataCurrent');

async function fadeOutAndRemoveMesh() {
    const dummy = new THREE.Object3D();
    for (let i = 0; i < instanceData.length; i++) {
        dummy.position.copy(instanceData[i].position);
        dummy.scale.set(0.0001, 0.0001, 0.0001);
        dummy.updateMatrix();
        instancedMesh.setMatrixAt(i, dummy.matrix);
    }
    instancedMesh.instanceMatrix.needsUpdate = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    instancedMesh.geometry.dispose();
    instancedMesh.material.dispose();
    scene.remove(instancedMesh);
}


initThree();
initEvents();
updateDatasetOptions(locationSelect.value);
animate();

function initThree() {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.id = "three-js-canvas";
    document.body.appendChild(renderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 0);
    controls.update();

    const ambientLight = new THREE.AmbientLight(0x404040, 30);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
    directionalLight.position.set(0, 30, 0);
    directionalLight.castShadow = true;
    scene.add(ambientLight, directionalLight);

    scene.background = loadCubemap();

    camera.position.set(0, 50, 220);
    camera.lookAt(0, 0, 0);

    stats.showPanel(0);
    stats.dom.style.position = 'absolute';
    stats.dom.style.bottom = '0px';
    stats.dom.style.left = '0px';
    document.body.appendChild(stats.dom);
}

function initEvents() {
    window.addEventListener('resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    });

    renderer.domElement.addEventListener('mousedown', onDocumentMouseDown, false);
    renderer.domElement.addEventListener('mousemove', onDocumentMouseMove, false);

    locationSelect.addEventListener('change', () => {
        const selectedLocation = locationSelect.value;
        updateDatasetOptions(selectedLocation);
        updateMiniMap(selectedLocation);
        resetCamera(); 
    });

    datasetSelect.addEventListener('change', () => {
        const location = locationSelect.value;
        const dataset = datasetSelect.value;
        loadCSV(location, dataset);
        resetCamera(); 
    });
}

function resetCamera() {
    const initialCameraPosition = { x: 0, y: 50, z: 220 };
    const initialTarget = { x: 0, y: 0, z: 0 };

    gsap.to(camera.position, {
        duration: 1.5,
        x: initialCameraPosition.x,
        y: initialCameraPosition.y,
        z: initialCameraPosition.z,
        ease: 'power2.inOut',
        onUpdate: () => controls.update()
    });

    gsap.to(controls.target, {
        duration: 1.5,
        x: initialTarget.x,
        y: initialTarget.y,
        z: initialTarget.z,
        ease: 'power2.inOut',
        onUpdate: () => controls.update(),
        onComplete: () => {
            resetCameraBtn?.classList?.add('hidden');
        }
    });
    
    resetInstances();

}

function resetInstances() {
    if (!instancedMesh) return;

    const dummy = new THREE.Object3D();
    instanceData.forEach((data, i) => {
        dummy.position.copy(data.position);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        instancedMesh.setMatrixAt(i, dummy.matrix);
        instancedMesh.setColorAt(i, data.originalColor);
    });

    instancedMesh.instanceMatrix.needsUpdate = true;
    instancedMesh.instanceColor.needsUpdate = true;

    selectedInstanceId = null;

    temperatureDisplay.textContent = 'NESSUNA';
    temperaturaSelezionataDataDiv.textContent = '--/--/---- --:--';
}
function sideView() {
    const sideCameraPosition = { x: 220, y: 50, z: 0 }; // 90° rispetto a z
    const sideTarget = { x: 0, y: 0, z: 0 };

    gsap.to(camera.position, {
        duration: 1.5,
        x: sideCameraPosition.x,
        y: sideCameraPosition.y,
        z: sideCameraPosition.z,
        ease: 'power2.inOut',
        onUpdate: () => controls.update()
    });

    gsap.to(controls.target, {
        duration: 1.5,
        x: sideTarget.x,
        y: sideTarget.y,
        z: sideTarget.z,
        ease: 'power2.inOut',
        onUpdate: () => controls.update()
    });

    resetInstances(); // opzionale: se vuoi anche azzerare la selezione
}



function updateDatasetOptions(location) {
    datasetSelect.innerHTML = '';
    datasetSelect.disabled = true;

    const datasets = datasetsPerLocation[location];
    if (datasets && datasets.length > 0) {
        datasets.forEach(file => {
            const option = document.createElement('option');
            option.value = file;
            option.textContent = file;
            datasetSelect.appendChild(option);
        });
        datasetSelect.disabled = false;
        loadCSV(location, datasets[0]);
    } else {
        const option = document.createElement('option');
        option.textContent = "Nessun dataset disponibile";
        datasetSelect.appendChild(option);
    }
}

function updateMiniMap(location) {
    const mapImg = document.getElementById('mini-map-image');
    mapImg.src = `maps/${location}.png`;
}

function animate() {
    stats.begin();
    requestAnimationFrame(animate);

    const time = Date.now() * 0.001;
    const dummy = new THREE.Object3D();

    if (instancedMesh) {
        instanceData.forEach((data, i) => {
            if (!data.visible) return;
            const pulse = Math.sin(time * data.pulseSpeed) * 0.5;
            const scale = (selectedInstanceId === i) ? 5 : 1 + Math.sin(time * data.pulseSpeed) * 0.2;

            dummy.position.copy(data.position);
            dummy.position.y = data.baseY + pulse;
            dummy.scale.set(scale, scale, scale);
            dummy.updateMatrix();

            instancedMesh.setMatrixAt(i, dummy.matrix);
        });
        instancedMesh.instanceMatrix.needsUpdate = true;
    }

    renderer.render(scene, camera);
    stats.end();
}

function onDocumentMouseMove(event) {
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
}

function onDocumentMouseDown(event) {
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(instancedMesh);
    if (intersects.length > 0) {
        const instanceId = intersects[0].instanceId;
        if (instanceId !== null) {
            if (selectedInstanceId !== null) {
                const previous = instanceData[selectedInstanceId];
                instancedMesh.setColorAt(selectedInstanceId, previous.originalColor);
            }

            selectedInstanceId = instanceId;
            const selected = instanceData[instanceId];
            instancedMesh.setColorAt(instanceId, new THREE.Color('#8ac440'));
            instancedMesh.instanceColor.needsUpdate = true;

            // UI
            temperatureDisplay.textContent = `${selected.temperature.toFixed(3)} °C`;
            temperaturaSelezionataDataDiv.textContent = `${selected.date}`;
            temperatureDisplay.style.display = 'block';
            temperaturaSelezionataDataDiv.style.display = 'block';

            // CAMERA ZOOM ANIMATO
            const offset = new THREE.Vector3(0, 10, 20); // offset rispetto al target
            const targetPosition = selected.position.clone().add(offset);

            gsap.to(camera.position, {
                duration: 1.5,
                x: targetPosition.x,
                y: targetPosition.y,
                z: targetPosition.z,
                ease: 'power2.inOut',
                onUpdate: () => {
                    camera.lookAt(selected.position); // opzionale
                    controls.update();
                }
            });

            gsap.to(controls.target, {
                duration: 1.5,
                x: selected.position.x,
                y: selected.position.y,
                z: selected.position.z,
                ease: 'power2.inOut',
                onUpdate: () => {
                    controls.update();
                }
            });

        }
    }
}

resetCameraBtn.addEventListener('click', resetCamera);
sideViewBtn.addEventListener('click', sideView);


function loadCubemap() {
    const loader = new THREE.CubeTextureLoader();
    return loader.load([
        '/dataviz/textures/cubemap/px.png', '/dataviz/textures/cubemap/nx.png',
        '/dataviz/textures/cubemap/py.png', '/dataviz/textures/cubemap/ny.png',
        '/dataviz/textures/cubemap/pz.png', '/dataviz/textures/cubemap/nz.png'
    ]);
}

function parseCSV(data) {
    const rows = data.split('\n').filter(row => row.trim());
    const headers = rows[0].split(',').map(h => h.trim());
    const tempIndex = headers.indexOf('Temperature');
    const dateIndex = headers.indexOf('Time');
    if (tempIndex === -1 || dateIndex === -1) return [];
    return rows.slice(1).map(row => {
        const cols = row.split(',');
        return {
            temperature: parseFloat(cols[tempIndex].trim()) || 0,
            date: cols[dateIndex].trim()
        };
    });
}

function showLoader() {
    document.getElementById('loader-overlay').classList.add('active');
}
function hideLoader() {
    document.getElementById('loader-overlay').classList.remove('active');
}

async function loadCSV(location = 'la_caletta', file = 'data.csv') {
    try {
        showLoader();
        const response = await fetch(`/dataviz/datasets/${location}/${file}`);
        const data = await response.text();
        if (data.includes('<!DOCTYPE html>')) throw new Error('File CSV non trovato');
        const parsed = parseCSV(data);

        if (instancedMesh) {
        await fadeOutAndRemoveMesh();
        const dummy = new THREE.Object3D();
        for (let i = 0; i < instanceData.length; i++) {
            dummy.position.copy(instanceData[i].position);
            dummy.scale.set(0.0001, 0.0001, 0.0001);
            dummy.updateMatrix();
            instancedMesh.setMatrixAt(i, dummy.matrix);
        }
        instancedMesh.instanceMatrix.needsUpdate = true;
        await new Promise(resolve => setTimeout(resolve, 300));
            instancedMesh.geometry.dispose();
            instancedMesh.material.dispose();
            scene.remove(instancedMesh);
        }

        instanceData = [];
        const temperatures = parsed.map(item => item.temperature);
        const minTemp = Math.min(...temperatures);
        const maxTemp = Math.max(...temperatures);
        const averageTemp = (temperatures.reduce((a, b) => a + b, 0) / temperatures.length).toFixed(2);
        temperaturaMediaDiv.textContent = `${averageTemp} °C`;

        const slider = document.getElementById('tempRange');
        if (slider.noUiSlider) slider.noUiSlider.destroy();
        noUiSlider.create(slider, {
            start: [minTemp, maxTemp],
            connect: true,
            step: 0.01,
            range: { min: minTemp, max: maxTemp },
            format: { to: v => v.toFixed(1), from: v => parseFloat(v) }
        });
        slider.noUiSlider.on('update', function (values) {
            document.getElementById('tempRangeValue').textContent = values.join(' - ');
            updateFilter(parseFloat(values[0]), parseFloat(values[1]));
        });

        const geometry = new THREE.SphereGeometry(0.6, 16, 16);
        const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5, metalness: 0.1 });
        instancedMesh = new THREE.InstancedMesh(geometry, material, parsed.length);
        instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        instancedMesh.castShadow = true;
        scene.add(instancedMesh);

        const dummy = new THREE.Object3D();
        const columnsPerRow = 24;
        const spacing = 5;

        parsed.forEach((item, index) => {
            const row = Math.floor(index / columnsPerRow);
            const col = index % columnsPerRow;
            const x = (col - columnsPerRow / 2) * spacing;
            const y = ((item.temperature - minTemp) / (maxTemp - minTemp)) * 20 + 5;
            const z = (row - Math.ceil(parsed.length / columnsPerRow) / 2) * spacing;
            dummy.position.set(x, y, z);
            dummy.scale.set(1, 1, 1);
            dummy.updateMatrix();
            const color = getColorForTemperature(item.temperature, minTemp, maxTemp);
            instancedMesh.setMatrixAt(index, dummy.matrix);
            instancedMesh.setColorAt(index, color);
            instanceData.push({
                temperature: item.temperature,
                date: item.date,
                pulseSpeed: 0.1 + Math.random(),
                baseY: y,
                position: new THREE.Vector3(x, y, z),
                visible: true,
                originalColor: color
            });
        });
        updateFilter(minTemp, maxTemp);
        resetInstances();

        const boundingBox = new THREE.Box3();
        instanceData.forEach(data => {
            boundingBox.expandByPoint(data.position);
        });
        const size = new THREE.Vector3();
        boundingBox.getSize(size);
        const maxDimension = Math.max(size.x, size.y, size.z);

        // aggiorna il far della camera
        camera.far = maxDimension * 3;
        camera.updateProjectionMatrix();


        setTimeout(() => hideLoader(), 500);
    } catch (e) {
        console.error("Errore nel caricamento CSV:", e);
    }
}

function updateFilter(min, max) {
    if (!instancedMesh) return;

    const dummy = new THREE.Object3D();
    instanceData.forEach((data, i) => {
        const visible = data.temperature >= min && data.temperature <= max;
        data.visible = visible;
        dummy.position.set(data.position.x, data.baseY, data.position.z);
        dummy.scale.set(visible ? 1 : 0.0001, visible ? 1 : 0.0001, visible ? 1 : 0.0001);
        dummy.updateMatrix();
        instancedMesh.setMatrixAt(i, dummy.matrix);
    });
    instancedMesh.instanceMatrix.needsUpdate = true;

    // ------ avvisi dinamici ------
    const avvisiDiv = document.getElementById('avvisi');

    let curiosita = "";

    if (max < 15) {
        curiosita = "Le temperature inferiori ai 15°C possono indicare correnti fredde o profondità maggiori. Lorem ipsum dolor sit amet.";
    } else if (min >= 15 && max <= 20) {
        curiosita = "Temperatura ideale per molte specie marine costiere. Lorem ipsum dolor sit amet.";
    } else if (min >= 20 && max <= 25) {
        curiosita = "Acque temperate, spesso registrate nei mesi estivi. Lorem ipsum dolor sit amet.";
    } else if (min >= 25) {
        curiosita = "Attenzione: temperature elevate possono segnalare stress termico per la fauna marina. Lorem ipsum dolor sit amet.";
    } else {
        curiosita = "Intervallo misto: analizza i dati per comprendere le variazioni. Lorem ipsum dolor sit amet.";
    }

    avvisiDiv.innerHTML = `<h4>Curiosità</h4>${curiosita}`;
}


function getColorForTemperature(temp, minTemp, maxTemp) {
    const n = (temp - minTemp) / (maxTemp - minTemp);
    if (n < 0.1) return new THREE.Color('#001219');
    if (n < 0.2) return new THREE.Color('#005f73');
    if (n < 0.3) return new THREE.Color('#0A9396');
    if (n < 0.4) return new THREE.Color('#94D2BD');
    if (n < 0.5) return new THREE.Color('#E9D8A6');
    if (n < 0.6) return new THREE.Color('#ECA72C'); 
    if (n < 0.7) return new THREE.Color('#F57429'); 
    if (n < 0.8) return new THREE.Color('#EE5622'); 
    if (n < 0.9) return new THREE.Color('#BF211E'); 
    return new THREE.Color('#8B0000');
}
